import { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { useQueryClient } from '@tanstack/react-query';
import { createIdea } from '../../api/dev';
import { useSnackbar } from '../../context/SnackbarContext';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function AddIdeaDialog({ open, onClose }: Props) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();

  const handleCreate = async () => {
    if (title.trim().length < 3) return;
    setLoading(true);
    setError(null);
    try {
      const wp = await createIdea(title.trim(), description.trim() || undefined);
      await queryClient.invalidateQueries({ queryKey: ['dev', 'wps'] });
      showSnackbar(`${wp.wp_number} idea saved`, 'success');
      handleClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to create idea';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <LightbulbIcon sx={{ color: 'warning.main' }} />
          Add Idea
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <TextField
          autoFocus
          label="Title"
          fullWidth
          placeholder="Minimum 3 characters"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={title.length > 0 && title.trim().length < 3}
          helperText={title.length > 0 && title.trim().length < 3 ? 'Title must be at least 3 characters' : ''}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Description"
          fullWidth
          multiline
          rows={4}
          placeholder="Optional details…"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<LightbulbIcon />}
          disabled={loading || title.trim().length < 3}
          onClick={() => void handleCreate()}
        >
          {loading ? <CircularProgress size={16} /> : 'Save Idea'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
